// CR085: Flyer Truth Table Types - Page lifecycle states and evidence structures
// CR173: Added OCR downscale outcomes and constants
// CR164: Added locator results type reference
import type { PageLocatorResults } from '@/lib/flyer-pipeline-v2/dealLocatorTypes';

// CR173: OCR size thresholds (configurable)
export const OCR_MAX_MEGAPIXELS = 40;       // Skip threshold (default: 40MP)
export const OCR_SAFE_TARGET_MEGAPIXELS = 20; // Target for downscale (default: 20MP)
export const OCR_MIN_WIDTH = 1400;          // Minimum width to preserve readability

export type PageOutcome = 
  | "DEAL_CREATED"          // Materialized deal from AI
  | "CANDIDATE"             // Passed signals, awaiting AI
  | "OCR_COMPLETE"          // OCR ran but not a candidate (dropped at signal threshold)
  | "OCR_SKIPPED"           // Page was skipped during OCR (e.g., too large, timeout)
  | "RENDERED"              // Page rendered but not OCR'd yet
  | "OCR_FAILED"            // OCR explicitly failed on this page
  | "RENDER_FAILED"         // Rendering failed
  | "PENDING"               // Not yet processed
  // CR173: New downscale outcomes
  | "OCR_DOWNSCALED_OK"     // OCR succeeded on downscaled image
  | "OCR_DOWNSCALED_EMPTY"  // OCR ran on downscaled but got no text
  | "OCR_DOWNSCALED_FAILED";// OCR failed on downscaled image

// CR173: OCR outcome for per-page tracking
export type OcrOutcome = 
  | "OCR_OK"
  | "OCR_EMPTY"
  | "OCR_FAILED"
  | "OCR_SKIPPED"
  | "OCR_DOWNSCALED_OK"
  | "OCR_DOWNSCALED_EMPTY"
  | "OCR_DOWNSCALED_FAILED";

// CR229: Drop reason categories for better diagnostics
export type DropReason = 
  | "BELOW_THRESHOLD"      // OCR'd but signal score below threshold
  | "OCR_SKIPPED_SIZE"     // Page too large for browser OCR
  | "OCR_SKIPPED_TIMEOUT"  // OCR timed out on this page
  | "OCR_NO_TEXT"          // OCR ran but extracted no text
  | "AI_NO_DEAL"           // AI scanned but found no deal
  | "AWAITING_OCR"         // Not yet OCR processed
  | "DOWNSCALE_FAILED"     // CR173: Downscaling failed
  | "UNKNOWN";             // Unknown reason

export interface TruthTablePage {
  id: string; // Unique identifier for the page (flyer_id + page_number)
  flyerId: string;
  flyerMerchant: string;
  pageNumber: number;
  
  // Render evidence
  rendered: boolean;
  renderPath?: string;
  renderWidth?: number;
  renderHeight?: number;
  
  // OCR evidence
  ocrComplete: boolean;
  ocrText?: string;         // Preview text (500 chars)
  ocrCharCount?: number;    // CR466: Full OCR character count from truth table
  ocrWordCount?: number;    // CR466: Word count from truth table
  ocrTextUrl?: string;      // Storage URL for full OCR text
  ocrConfidence?: number;
  ocrDurationMs?: number;
  
  // CR161: AI-derived OCR / vision text (separate from Tesseract)
  aiOcrText?: string;       // AI-derived text (may be better quality)
  aiOcrModel?: string;      // Model used for AI OCR
  aiOcrCallId?: string;     // AI call ID for linking
  
  // Signal evidence
  signalScore?: number;
  signalThreshold?: number;
  triggeredSignals?: string[];
  signalBreakdown?: unknown;
  
  // Candidate status
  isCandidate: boolean;
  
  // AI evidence
  aiScanned: boolean;
  aiDealExtracted: boolean;
  aiDealSummary?: string;
  aiModel?: string;
  aiDurationMs?: number;
  // CR092: AI call audit
  aiCallId?: string;
  promptStep?: string;
  // CR158: AI timeout tracking
  aiTimeoutMs?: number;
  aiActivePhase?: string;
  aiPhaseTimings?: Record<string, number | null>;
  aiTimedOut?: boolean;
  
  // CR161: Raw AI output for JSON viewer
  aiRawOutput?: unknown;
  
  // Final outcome
  outcome: PageOutcome;
  outcomeReason: string;
  
  // CR229: Enhanced drop tracking
  dropReason?: DropReason;
  ocrSkipped?: boolean;         // True if this page was never OCR'd
  ocrSkipReason?: string;       // Why OCR was skipped (e.g., "Page too large")
  
  // CR173: OCR input tracking (which image variant was used)
  ocrInputVariant?: "full" | "ocr_safe";  // Which image was used for OCR
  ocrOutcome?: OcrOutcome;                 // Detailed OCR outcome
  ocrSafeWidth?: number;                   // Downscaled dimensions
  ocrSafeHeight?: number;
  ocrSafeMegapixels?: number;
  renderMegapixels?: number;               // Full render megapixels
  ocrEngineUsed?: string;                  // Which engine performed OCR
  
  // CR092: Error tracking for Truth Table
  hasError?: boolean;
  errorCode?: string;
  errorMessage?: string;
  
  // Links
  stagingDealId?: string;
  
  // CR160: Multi-brand variant tracking
  isMultiBrandVariant?: boolean;
  multiBrandVariantCount?: number;
  variantNotes?: string;

  // CR164: Deal Locator results
  locatorResults?: PageLocatorResults;
}

export interface FlyerSummary {
  flyerId: string;
  merchantKey: string;
  pageCount: number;
  renderedPages: number;
  ocrPages: number;
  candidatePages: number;
  aiScannedPages: number;
  dealsCreated: number;
}

export interface TruthTableSummary {
  totalFlyers: number;
  totalPages: number;
  ocrPages: number;
  candidatePages: number;
  aiCalls: number;
  dealsCreated: number;
  discardedPages: number;
}

export interface PageEvidence {
  // Render
  renderImageUrl?: string;
  renderWidth?: number;
  renderHeight?: number;
  
  // OCR (Tesseract)
  ocrText?: string;
  ocrConfidence?: number;
  ocrDurationMs?: number;
  
  // CR161: AI-derived OCR
  aiOcrText?: string;
  aiOcrModel?: string;
  aiOcrCallId?: string;
  
  // Signals
  signals?: Array<{
    name: string;
    score: number;
    matched: boolean;
  }>;
  totalScore?: number;
  threshold?: number;
  
  // AI
  aiPromptPack?: string;
  aiPromptVersion?: string;
  aiModel?: string;
  aiOutput?: Record<string, unknown>;
  aiValidationResult?: "pass" | "fail";
  aiValidationErrors?: string[];
  // CR161: Raw AI output for JSON viewer
  aiRawOutput?: unknown;
  aiCallId?: string;
}
